// secret-match-intervals.ts — review 04a M2/L4 (spec V2 §4.2 (13)).
//
// Che theo KHOẢNG trên text gốc thay vì thay tuần tự từng mẫu:
//  - tìm MỌI lần xuất hiện (kể cả chồng lấn) của mọi literal known-value và
//    mọi match pattern chung trên text THÔ, rồi gộp các khoảng giao nhau ⇒ value
//    ngắn là tiền tố của value dài, hay đuôi A = đầu B, đều bị che cả hợp (thay
//    tuần tự sẽ thay A trước và để lộ phần còn lại của B);
//  - nới mỗi khoảng cho trọn escape JSON ở hai đầu (`\"`, `\\`, `\n`,
//    `\uXXXX`): khoảng kết thúc ngay sau một `\` mở escape hay bắt đầu giữa một
//    escape sẽ để lại escape mồ côi (`[REDACTED]\"` đóng chuỗi sớm, `\[` sai
//    cú pháp) ⇒ dòng stream-json vỡ ⇒ result mất. Nới thừa vài ký tự là vô hại.
export type MatchInterval = readonly [start: number, end: number];

export const REDACTED_PLACEHOLDER = "[REDACTED]";
const UNICODE_ESCAPE_PREFIX = /\\u[0-9a-fA-F]{0,3}$/;

/** Số `\` liên tiếp kết thúc TẠI vị trí `index` (tính cả nó), đếm lùi. */
function backslashRunEndingAt(text: string, index: number): number {
  let i = index;
  while (i >= 0 && text.charCodeAt(i) === 92) i--;
  return index - i;
}

function opensEscape(text: string, backslashIndex: number): boolean {
  return backslashIndex >= 0 && backslashRunEndingAt(text, backslashIndex) % 2 === 1;
}

function widenToWholeJsonEscapes(text: string, [s, e]: MatchInterval): MatchInterval {
  let start = s;
  const before = UNICODE_ESCAPE_PREFIX.exec(text.slice(Math.max(0, s - 5), s));
  if (before && opensEscape(text, s - before[0].length)) start = s - before[0].length;
  else if (opensEscape(text, s - 1)) start = s - 1;

  let end = e;
  if (e > start && opensEscape(text, e - 1)) {
    end = Math.min(text.length, text[e] === "u" ? e + 5 : e + 1);
  } else {
    const inside = UNICODE_ESCAPE_PREFIX.exec(text.slice(Math.max(start, e - 5), e));
    if (inside && opensEscape(text, e - inside[0].length)) {
      end = Math.min(text.length, e - inside[0].length + 6);
    }
  }
  return [start, end];
}

/**
 * Vòng sửa 3 (review M1): lùi điểm cắt stream ra NGOÀI escape JSON — phần đã
 * phát không bao giờ kết thúc bằng `\` mở escape hay giữa `\uXXXX`. Nhờ vậy
 * buffer kế tiếp bắt đầu đúng ranh giới escape và phép đếm parity `\` (chỉ
 * trong buffer) luôn đúng theo quy nạp; cắt giữa `\\` từng làm chunk sau nới
 * nhầm thành `\[REDACTED]` ⇒ dòng JSON vỡ.
 */
export function alignCutOutsideJsonEscape(text: string, cut: number): number {
  const u = UNICODE_ESCAPE_PREFIX.exec(text.slice(Math.max(0, cut - 5), cut));
  if (u && opensEscape(text, cut - u[0].length)) return cut - u[0].length;
  return opensEscape(text, cut - 1) ? cut - 1 : cut;
}

/** Mọi lần xuất hiện (chồng lấn được) của từng literal. */
export function literalMatchIntervals(text: string, literals: readonly string[]): MatchInterval[] {
  const out: MatchInterval[] = [];
  for (const literal of literals) {
    if (!literal) continue;
    for (let i = text.indexOf(literal); i !== -1; i = text.indexOf(literal, i + 1)) {
      out.push([i, i + literal.length]);
    }
  }
  return out;
}

/**
 * Match pattern chung (`/g`). `finalize = false`: bỏ match chạm cuối text — có
 * thể chỉ là đầu của token còn đang tới (stream); đoạn đó nằm trong đuôi giữ lại.
 */
export function patternMatchIntervals(
  text: string,
  patterns: readonly RegExp[],
  finalize: boolean,
): MatchInterval[] {
  const out: MatchInterval[] = [];
  for (const pattern of patterns) {
    for (const m of text.matchAll(pattern)) {
      const end = m.index + m[0].length;
      if (finalize || end < text.length) out.push([m.index, end]);
    }
  }
  return out;
}

/** Nới cho trọn escape rồi gộp khoảng giao/kề nhau, sắp theo đầu. */
export function mergeRedactionIntervals(
  text: string,
  intervals: readonly MatchInterval[],
): MatchInterval[] {
  const sorted = intervals
    .map((i) => widenToWholeJsonEscapes(text, i))
    .sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const merged: Array<[number, number]> = [];
  for (const [s, e] of sorted) {
    const last = merged[merged.length - 1];
    if (last !== undefined && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  }
  return merged;
}

/** Thay mỗi khoảng (đã gộp, sắp) bằng placeholder. */
export function applyRedactionIntervals(text: string, intervals: readonly MatchInterval[]): string {
  let out = "";
  let cursor = 0;
  for (const [s, e] of intervals) {
    out += text.slice(cursor, s) + REDACTED_PLACEHOLDER;
    cursor = e;
  }
  return out + text.slice(cursor);
}
